import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { SOCIAL_LINKS } from "../../utils/socialLinks";
import "./BannerSlider.css";

const PRELOADED_BANNER_URLS_THIS_LOAD = new Set();

const BannerSlider = () => {
  const [banners, setBanners] = useState([]);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [showChannelBanner, setShowChannelBanner] = useState(true);

  // slider state
  const [isDesktop, setIsDesktop] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const autoSlideRef = useRef(null);
  const transitionTimeoutRef = useRef(null);
  const isTransitioningRef = useRef(false);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  // detect desktop vs mobile
  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 992);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const buildBannerUrl = (banner) => {
    if (!banner) return "";

  // already absolute url
    if (banner.imageUrl) return banner.imageUrl;

  // API already sends "/banners/xxx.jpg"
    if (banner.image && banner.image.startsWith("/")) return banner.image;

  // fallback (older DB values)
    if (banner.image && banner.image.startsWith("http")) return banner.image;

    return "";
  };


  // fetch banners
  async function getBanners() {
    try {
      setBannerLoading(true);
      const res = await fetch("/api/banner/get-banners");
      if (!res.ok) throw new Error("Banner API failed");
      const json = await res.json();
      if (json.success && Array.isArray(json.data)) setBanners(json.data);
      else setBanners([]);
    } catch (e) {
      setBanners([]);
    } finally {
      setBannerLoading(false);
    }
  }

  useEffect(() => {
    getBanners();
  }, []);

  // preload all banners
  useEffect(() => {
    if (!Array.isArray(banners) || banners.length === 0) return;

    for (const b of banners) {
      const url = buildBannerUrl(b);
      if (!url) continue;
      if (PRELOADED_BANNER_URLS_THIS_LOAD.has(url)) continue;

      const img = new Image();
      img.decoding = "async";
      img.src = url;
      PRELOADED_BANNER_URLS_THIS_LOAD.add(url);
    }
  }, [banners]);

  // visible banners
  const getVisibleBanners = () => {
    if (banners.length === 0) return [];
    const result = [];

    if (isDesktop) {
      if (banners.length === 1) {
        result.push({ ...banners[0], position: "center" });
      } else if (banners.length === 2) {
        const otherIndex = (currentSlide + 1) % 2;
        result.push(
          { ...banners[currentSlide], position: "center" },
          { ...banners[otherIndex], position: "right" }
        );
      } else {
        const prevIndex = (currentSlide - 1 + banners.length) % banners.length;
        const nextIndex = (currentSlide + 1) % banners.length;
        result.push(
          { ...banners[prevIndex], position: "left" },
          { ...banners[currentSlide], position: "center" },
          { ...banners[nextIndex], position: "right" }
        );
      }
    } else {
      result.push({ ...banners[currentSlide], position: "center" });
    }

    return result;
  };

  const goSlide = (direction) => {
    if (isTransitioningRef.current || banners.length === 0) return;

    isTransitioningRef.current = true;
    setIsTransitioning(true);

    setCurrentSlide((prev) => {
      const len = banners.length;
      if (len <= 1) return prev;
      if (direction === "next") return (prev + 1) % len;
      return (prev - 1 + len) % len;
    });

    clearTimeout(transitionTimeoutRef.current);
    transitionTimeoutRef.current = setTimeout(() => {
      isTransitioningRef.current = false;
      setIsTransitioning(false);
    }, 500);
  };

  const startAutoSlide = () => {
    clearInterval(autoSlideRef.current);
    if (banners.length <= 1) return;
    autoSlideRef.current = setInterval(() => goSlide("next"), 5000);
  };

  const manualSlide = (direction) => {
    goSlide(direction);
    startAutoSlide();
  };

  useEffect(() => {
    startAutoSlide();
    return () => clearInterval(autoSlideRef.current);
  }, [banners.length]);

  useEffect(() => {
    if (banners.length > 0) setCurrentSlide(0);
  }, [banners.length]);

  useEffect(() => {
    return () => {
      clearInterval(autoSlideRef.current);
      clearTimeout(transitionTimeoutRef.current);
    };
  }, []);

  const renderDots = () => {
    if (banners.length <= 1 || isDesktop) return null;

    return (
      <div className="home-banner-dots">
        {banners.map((_, idx) => (
          <span
            key={idx}
            className={`home-banner-dot ${idx === currentSlide ? "active" : ""}`}
            onClick={() => {
              setCurrentSlide(idx);
              startAutoSlide();
            }}
          />
        ))}
      </div>
    );
  };

  return (
    <section className="home-banner-section">
      {showChannelBanner && (
        <div className="home-channel-banner">
          <a
            className="home-channel-link"
            href={SOCIAL_LINKS.whatsappChannel}
            target="_blank"
            rel="noreferrer"
          >
            <span className="home-channel-icon">
              <WhatsAppIcon />
            </span>
            <span className="home-channel-copy">
              <strong>Join our WhatsApp channel!</strong>
              <span>Get top-up updates, offers, and fast support.</span>
            </span>
          </a>
          <button
            type="button"
            className="home-channel-close"
            onClick={() => setShowChannelBanner(false)}
            aria-label="Hide WhatsApp channel banner"
          >
            <CloseIcon />
          </button>
        </div>
      )}

      {bannerLoading && (
        <div className="home-banner-skeleton">
          <div className="home-banner-skeleton-shine" />
        </div>
      )}

      {!bannerLoading && banners.length > 0 && (
        <div className="home-banner-container">
          <div className="home-banner-wrapper">
            {banners.length > 1 && (
              <button
                className="home-banner-nav prev"
                onClick={() => manualSlide("prev")}
                disabled={isTransitioning}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 18L9 12L15 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}

            <div className="home-banner-track">
              {getVisibleBanners().map((banner, index) => (
                <div
                  className={`home-banner-item ${banner.position} ${
                    isTransitioning ? "transitioning" : ""
                  }`}
                  key={`${banner._id || buildBannerUrl(banner) || "banner"}-${index}`}
                  onClick={() => {
                    if (banner.link && banner.position === "center") {
                      window.open(banner.link, "_blank");
                    }
                  }}
                  style={{
                    cursor: banner.position === "center" && banner.link ? "pointer" : "default",
                  }}
                >
                  <div className="banner-image-wrapper">
                    <img
                      src={buildBannerUrl(banner)}
                      alt={banner.heading || banner.tag || "Banner"}
                      width="4096"
                      height="1556"
                      decoding="async"
                      loading={banner.position === "center" ? "eager" : "lazy"}
                      fetchPriority={banner.position === "center" ? "high" : "auto"}
                    />
                  </div>
                </div>
              ))}
            </div>

            {banners.length > 1 && (
              <button
                className="home-banner-nav next"
                onClick={() => manualSlide("next")}
                disabled={isTransitioning}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M9 18L15 12L9 6"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
          </div>

          {renderDots()}
        </div>
      )}

      {!bannerLoading && banners.length === 0 && (
        <div className="no-banners-message">
          <p>No banners available at the moment</p>
        </div>
      )}
    </section>
  );
};

export default BannerSlider;
